// A20 W7a — the two indicators triage cannot function without, shared by the
// queue and the detail view so they can never drift apart.
//
//   MaskBanner    — is the reviewer looking at redacted text or real customer
//                   text? That question has to be answered before anyone
//                   presses play, not in a tooltip afterwards.
//   GithubSyncChip — did this ticket actually reach GitHub? A stuck ticket must
//                   LOOK stuck. github-issues.service.ts writes every outcome
//                   back to `github_sync_state`, including the failures,
//                   precisely so this chip has something honest to show.

import * as React from 'react';
import {
  AlertTriangle, CheckCircle2, Clock3, EyeOff, ExternalLink, HelpCircle, MinusCircle,
  ShieldCheck, ShieldAlert, XCircle,
} from 'lucide-react';
import { Pill } from '@/components/shared/badges';
import type { GithubSyncState, MaskingVerdict, TicketStatus } from './api';

// Ticket lifecycle. Not StatusBadge: its config has no entry for `triaged`,
// `in_progress` or `closed`, and its fallback would render "In_progress".
const STATUS_SKIN: Record<TicketStatus, { bg: string; fg: string; label: string }> = {
  open: { bg: '#DBEAFE', fg: '#1D4ED8', label: 'Open' },
  triaged: { bg: '#E0E7FF', fg: '#4338CA', label: 'Triaged' },
  in_progress: { bg: '#FEF9C3', fg: '#A16207', label: 'In progress' },
  resolved: { bg: '#DCFCE7', fg: '#15803D', label: 'Resolved' },
  closed: { bg: 'var(--bg-muted)', fg: 'var(--text-secondary)', label: 'Closed' },
};

export function StatusPill({ status }: { status: TicketStatus | string }) {
  const s = STATUS_SKIN[status as TicketStatus];
  if (!s) return <Pill bg="var(--bg-muted)" fg="var(--text-secondary)">{String(status).replace(/_/g, ' ')}</Pill>;
  return <Pill bg={s.bg} fg={s.fg}>{s.label}</Pill>;
}

/**
 * The same word the pill uses, for prose — the transition control's buttons and
 * the timeline's "Open → Triaged" lines.
 *
 * Exported so those two cannot invent a third spelling of `in_progress`. The
 * fallback un-snakes an unknown value rather than dropping it: a status this
 * build has never heard of is exactly the thing an operator must be able to see.
 */
export function statusLabel(status: TicketStatus | string): string {
  return STATUS_SKIN[status as TicketStatus]?.label ?? String(status).replace(/_/g, ' ');
}

/** Ditto for the OTHER state machine sharing support_ticket_events. */
const SYNC_STATE_LABEL: Record<GithubSyncState, string> = {
  pending: 'Queued for GitHub',
  synced: 'Filed in GitHub',
  failed: 'GitHub sync failed',
  skipped: 'Not filed (skipped)',
};

export function syncStateLabel(state: GithubSyncState | string): string {
  return SYNC_STATE_LABEL[state as GithubSyncState] ?? String(state).replace(/_/g, ' ');
}

// A ticket that has sat 'pending' longer than this has almost certainly not
// been picked up by the sync sweep. The sweep is serial and rate-limit-aware,
// so minutes are normal and an hour is not.
const PENDING_STUCK_MS = 60 * 60 * 1000;

// ═══════════════════════════════════════════════════════════════════
// THE CLAIM, THE FACT, AND THE GAP BETWEEN THEM (A20 phase 1b).
// ─────────────────────────────────────────────────────────────────
// `masked` is a CLIENT-SUPPLIED boolean: bug-report.service.ts derives it from
// `envelope.report.includeVisibleText`, i.e. from a checkbox in a browser we do
// not control. This banner once turned that into a flat claim about the bytes —
// "Nothing legible in this replay is real customer text" — which the row cannot
// support, and then retreated to the word "unverified", which was honest but
// useless: it told a reviewer nothing they could act on.
//
// The server now checks the stored capture at ingest
// (backend/src/services/capture-masking.verify.ts) and PERSISTS the verdict
// (migration 41 SECTION 11). So this component renders both:
//
//   the CLAIM  — what the reporter said they consented to;
//   the FACT   — what the server found in the bytes.
//
// and it says so LOUDLY when they disagree, because a ticket that says
// "redacted" over a capture full of names is precisely the hazard this whole
// mechanism exists to catch, and it is invisible unless something draws it.
//
// TWO THINGS THIS DELIBERATELY STILL WILL NOT DO:
//   1. It does not upgrade 'verified_masked' into "safe". That verdict is a
//      SAMPLE over TEXT NODES; rrweb does not mask attributes, so `alt`,
//      `title`, `aria-label`, `placeholder` and every URL survive in both
//      postures. The sample size and the truncation flag are rendered with it
//      for the same reason — a verdict without its denominator is a boast.
//   2. It does not soften the REVEALED side on a server verdict it likes.
//      Over-warning a reviewer that they are looking at customer data costs
//      nothing; under-warning them is the entire hazard.
// ═══════════════════════════════════════════════════════════════════

/** Everything the two components need. The last four may be absent — see api.ts. */
export interface MaskEvidence {
  /** THE CLAIM. Always present: it is a NOT NULL column written at ingest. */
  masked: boolean;
  /** THE FACT. null/undefined = nothing was checked, which is not an answer. */
  verdict?: MaskingVerdict | null;
  sampled?: number | null;
  legible?: number | null;
  truncated?: boolean | null;
  /**
   * False when no recording was stored. Then there is no artefact anyone could
   * have checked, and "not checked" would imply a check we skipped.
   */
  hasCapture?: boolean;
}

type Tone = 'hazard' | 'caution' | 'confirmed' | 'unknown';

interface MaskReading {
  tone: Tone;
  headline: string;
  detail: string;
  /** True when the claim and the verdict do not agree. Drives the flag. */
  divergent: boolean;
}

const TONE_SKIN: Record<Tone, { bg: string; border: string; fg: string; icon: React.ElementType; iconColor: string }> = {
  hazard: { bg: '#FEE2E2', border: '#FECACA', fg: '#7F1D1D', icon: ShieldAlert, iconColor: '#DC2626' },
  caution: { bg: '#FEF3C7', border: '#FDE68A', fg: '#78350F', icon: AlertTriangle, iconColor: '#D97706' },
  confirmed: { bg: '#DCFCE7', border: '#BBF7D0', fg: '#14532D', icon: ShieldCheck, iconColor: '#15803D' },
  unknown: { bg: 'var(--bg-muted)', border: 'var(--border)', fg: 'var(--text-secondary)', icon: EyeOff, iconColor: 'var(--text-muted)' },
};

/** "3 of 120 sampled values", with the truncation caveat when there is one. */
function scope(e: MaskEvidence): string {
  const n = e.sampled ?? 0;
  const of = `${n} sampled text value${n === 1 ? '' : 's'}`;
  return e.truncated
    ? `${of} (a cap stopped the check before the end of the stream, so it covers only the beginning)`
    : of;
}

/**
 * Turn the pair into one sentence a reviewer can act on.
 *
 * Exported because the queue pill and the detail banner must never disagree
 * about what a row means, and because it is the piece worth testing directly.
 */
export function readMaskEvidence(e: MaskEvidence): MaskReading {
  const revealedCopy =
    'The reporter consented to include the text that was visible on their screen. Typed values stay masked. Treat this replay as customer data.';

  switch (e.verdict) {
    // ── The hazard, and the one case that gets the loudest skin on the page ──
    case 'contradicted':
      return e.masked
        ? {
            tone: 'hazard',
            headline: 'Reporter said redacted — the capture is legible',
            divergent: true,
            detail:
              `The server read the stored capture and found ${e.legible ?? 0} of ${scope(e)} still reading as ordinary prose. ` +
              'The redaction claim on this ticket is FALSE for these bytes. Treat the replay as customer data, and consider whether it should be erased rather than replayed.',
          }
        : {
            tone: 'caution',
            headline: 'Real customer text — confirmed in the capture',
            divergent: false,
            detail: `${revealedCopy} The server found legible text in the stored bytes, exactly as the reporter said it would.`,
          };

    case 'verified_masked':
      return e.masked
        ? {
            tone: 'confirmed',
            headline: 'Redacted — checked against the stored capture',
            divergent: false,
            detail:
              `The server inflated the capture and found all ${scope(e)} masked. ` +
              'That covers TEXT ONLY: the recorder never masks attributes, so alt text, titles, placeholders and every URL in the replay are real. Do not treat this as "no customer data".',
          }
        : {
            tone: 'caution',
            headline: 'Claim and capture disagree — the capture is masked',
            divergent: true,
            detail:
              `The reporter consented to include visible text, but all ${scope(e)} in the stored capture are masked. ` +
              'Nothing here is more exposed than they intended — but the recorder did not do what they asked, so the replay may not show the thing they were trying to show you.',
          };

    // ── Checked, and honestly unable to say. Not a pass, and not a failure. ──
    case 'inconclusive':
      return e.masked
        ? {
            tone: 'unknown',
            headline: 'Reporter says redacted — the server could not judge',
            divergent: false,
            detail:
              `The server read the capture but could only use ${scope(e)}, which is not enough to support or contradict the claim. ` +
              'Open it expecting redaction, but do not rely on it.',
          }
        : { tone: 'caution', headline: 'Real customer text', divergent: false, detail: revealedCopy };

    // ── There are bytes, and they are not readable. Says nothing either way. ──
    case 'unverifiable':
      return e.masked
        ? {
            tone: 'unknown',
            headline: 'Reporter says redacted — the capture could not be read',
            divergent: false,
            detail:
              'The server could not turn the stored capture into an event stream, so it has nothing to say about it in either direction — this is not evidence for the claim and not evidence against it. ' +
              'It is also why the replay below may refuse to load.',
          }
        : { tone: 'caution', headline: 'Real customer text', divergent: false, detail: revealedCopy };

    // ── No verdict at all. Three different reasons, and they are not the same ──
    default:
      if (!e.masked) {
        return { tone: 'caution', headline: 'Real customer text', divergent: false, detail: revealedCopy };
      }
      if (e.hasCapture === false) {
        return {
          tone: 'unknown',
          headline: 'Reporter says redacted — no capture to check',
          divergent: false,
          detail:
            'No recording was stored for this report, so there are no bytes for the server to have checked. The claim covers the console and network extracts below, which the recorder redacts by the same rules.',
        };
      }
      return {
        tone: 'unknown',
        headline: 'Reporter says redacted — not checked',
        divergent: false,
        detail:
          'The reporter’s browser reported this capture as masked and no server verdict was recorded for it — the check was switched off, or this ticket predates it, or the licence server is running ahead of its database. Treat legible text as possible until you have looked.',
      };
  }
}

export function MaskBanner({ compact, ...evidence }: MaskEvidence & { compact?: boolean }) {
  const reading = readMaskEvidence(evidence);
  const skin = TONE_SKIN[reading.tone];
  const IconCmp = skin.icon;
  return (
    <div
      role="note"
      data-masked={evidence.masked}
      data-verdict={evidence.verdict ?? 'none'}
      data-divergent={reading.divergent}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: compact ? '8px 12px' : '12px 16px',
        background: skin.bg, border: `1px solid ${skin.border}`, borderRadius: 'var(--radius-md)', color: skin.fg,
      }}
    >
      <IconCmp size={16} color={skin.iconColor} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ lineHeight: 1.45 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>
          {reading.headline}
        </div>
        <div style={{ fontSize: 12.5 }}>{reading.detail}</div>
        {/* The divergence is called out as its OWN line rather than left for a
            reader to infer from two adjacent labels. Both directions get it: one
            is a data-exposure hazard, the other is a recorder that ignored
            consent, and neither should have to be noticed to be noticed. */}
        {reading.divergent && (
          <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4 }}>
            Reporter’s claim: {evidence.masked ? 'redacted' : 'real text'} · Server found:{' '}
            {evidence.verdict === 'contradicted' ? 'legible text' : 'masked text'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The one-word version, for a table cell.
 *
 * A queue is scanned, not read, so the only job here is to make the two rows a
 * triager must not miss — the contradicted claim and the mismatch — look
 * different from the 200 that are fine. Everything subtler than that is left to
 * the banner on the detail page, which has room for the sentence.
 *
 * "Redacted?" (with the question mark) survives as the NO-VERDICT rendering, for
 * the original reason: with nothing but the client's claim, a question mark is
 * the shortest honest thing that fits in a cell.
 */
export function MaskPill(evidence: MaskEvidence) {
  const { masked, verdict } = evidence;

  if (masked && verdict === 'contradicted') {
    return <Pill bg="#FEE2E2" fg="#DC2626"><ShieldAlert size={10} /> Claim false</Pill>;
  }
  if (!masked && verdict === 'verified_masked') {
    return <Pill bg="#FFEDD5" fg="#C2410C"><AlertTriangle size={10} /> Claim mismatch</Pill>;
  }
  if (!masked) {
    return <Pill bg="#FEF3C7" fg="#92400E">Real text</Pill>;
  }
  if (verdict === 'verified_masked') {
    return <Pill bg="#DCFCE7" fg="#15803D"><ShieldCheck size={10} /> Redacted</Pill>;
  }
  if (verdict === 'inconclusive' || verdict === 'unverifiable') {
    return <Pill bg="var(--bg-muted)" fg="var(--text-secondary)"><HelpCircle size={10} /> Unconfirmed</Pill>;
  }
  return <Pill bg="var(--bg-muted)" fg="var(--text-secondary)">Redacted?</Pill>;
}

export interface GithubSyncChipProps {
  state: GithubSyncState;
  url: string | null;
  repo: string | null;
  issueNumber: number | null;
  error: string | null;
  /** Ticket created_at — drives the "pending too long" call-out. */
  createdAt?: string;
  /** Adds the failure/skip reason underneath. Detail view only. */
  verbose?: boolean;
}

export function GithubSyncChip({
  state, url, repo, issueNumber, error, createdAt, verbose,
}: GithubSyncChipProps) {
  // A URL is proof the issue exists; prefer it over the state column, which the
  // sweep could in principle have failed to update after creating the issue
  // (github-issues.service.ts logs exactly that case as a duplicate risk).
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}
      >
        <CheckCircle2 size={13} />
        {repo && issueNumber ? `${repo}#${issueNumber}` : 'GitHub issue'}
        <ExternalLink size={11} />
      </a>
    );
  }

  const stalled =
    state === 'pending' && !!createdAt && Date.now() - new Date(createdAt).getTime() > PENDING_STUCK_MS;

  const skin: Record<GithubSyncState, { bg: string; fg: string; label: string; icon: React.ElementType }> = {
    pending: stalled
      ? { bg: '#FFEDD5', fg: '#C2410C', label: 'Sync stuck', icon: AlertTriangle }
      : { bg: '#FEF9C3', fg: '#A16207', label: 'Sync pending', icon: Clock3 },
    failed: { bg: '#FEE2E2', fg: '#DC2626', label: 'Sync failed', icon: XCircle },
    skipped: { bg: 'var(--bg-muted)', fg: 'var(--text-secondary)', label: 'Sync skipped', icon: MinusCircle },
    // 'synced' with no URL is a contradiction the row cannot resolve on its
    // own; say so rather than implying a link that is not there.
    synced: { bg: '#FEE2E2', fg: '#DC2626', label: 'Synced, link missing', icon: AlertTriangle },
  };
  const s = skin[state] ?? skin.pending;
  const IconCmp = s.icon;

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
      <Pill bg={s.bg} fg={s.fg}><IconCmp size={10} /> {s.label}</Pill>
      {verbose && stalled && (
        <span style={{ fontSize: 11.5, color: '#C2410C' }}>
          Queued since {new Date(createdAt!).toLocaleString()} — the sync sweep has not picked it up.
        </span>
      )}
      {verbose && error && (
        <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', maxWidth: 520, wordBreak: 'break-word' }}>
          {error}
        </span>
      )}
      {verbose && !error && state === 'skipped' && (
        <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
          No reason recorded. GitHub issues are usually skipped because the integration is unconfigured.
        </span>
      )}
    </div>
  );
}
